import { gql } from '@apollo/client';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { Dialog } from '@headlessui/react';
import { Auth } from 'aws-amplify';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LOGIN_PATH } from 'src/common/constants';
import images from 'src/common/images';
import { MstOffice, MutationSet } from 'src/common/interfaces';
import { RecordState } from 'src/common/string-enum';
import { ErrorMessage, InfoMessage, InfoWord } from 'src/enum/messsage-enum';
import { deleteMstUser, insertMstUser, updateMstUser } from 'src/graphql/mutations';
import { getUserInfo } from 'src/graphql/queries';
import { useAppLazyQuery, useAppMutation } from 'src/libs/hooks';
import utility from 'src/libs/utility';
import ModalGuideOffice from '../guide-office/guide-office';
import MainLayout from '../main-layout';

const header = ['メール', 'コード', '名称', '拠点', '管理者', '削除'];

interface ErrorMstUser {
  isErrorUserCd: boolean;
  isErrorUserNm: boolean;
  isErrorMstMail: boolean;
}

export interface MstUser {
  mu_mail: string;
  mo_office_nm: string;
  mu_office_cd: number | null;
  mu_user_nm: string;
  mu_user_id: number | null;
  mu_admin_kb: number;
  isCannotEditUserCd: boolean;
  recordState?: RecordState;
  error: ErrorMstUser;
  isBeginData: boolean;
}
const defaultErrorMstUser = {
  isErrorUserCd: false,
  isErrorUserNm: false,
  isErrorMstMail: false,
};

const emptyUser = {
  mu_mail: '',
  mu_office_cd: null,
  mu_user_id: null,
  mu_admin_kb: 0,
  mo_office_nm: '',
  mu_user_nm: '',
  isCannotEditUserCd: false,
  recordState: RecordState.INSERT,
  error: defaultErrorMstUser,
  isBeginData: false,
};

type ModalProps = {
  open: boolean;
  index?: number;
};

const UserManagement = () => {
  const { signOut } = useAuthenticator((context) => [context.user]);
  const { confirmAlert, showAlertMessage, registerData } = utility;
  const [beginLengthListUser, setBeginLengthListUser] = useState<number>(0);
  const [mstUser, setMstUser] = useState<MstUser[]>([]);
  const [deleteFunc] = useAppMutation({ query: gql(deleteMstUser) });
  const [updateFunc] = useAppMutation({ query: gql(updateMstUser) });
  const [insertFunc] = useAppMutation({ query: gql(insertMstUser) });
  const [modal, setModal] = useState<ModalProps>({ open: false });
  const navigate = useNavigate();
  const location = useLocation();
  const [removedMstUser, setRemovedMstUser] = useState<MstUser[]>([]);

  const [searchMstUser, { data: mstUserData, loading: isMstUserLoading }] = useAppLazyQuery({
    query: gql(getUserInfo),
    onCompleted: () => {
      setRemovedMstUser([]);
    },
  });

  const renderTableHeader = useMemo(() => {
    return header.map((key, index) => (
      <th
        className="p-[8px] border-[1px] border-solid border-[#fff] pt-[12px] pb-[12px] text-center bg-[#a8d08d] text-[#fff]"
        key={index}
      >
        {key.toUpperCase()}
      </th>
    ));
  }, []);

  const renderTableData = useMemo(() => {
    if (!mstUser || mstUser.length <= 0) return;
    const styleInput = (isError: boolean | undefined) => {
      return `${
        isError ? 'border-[#FF0000] border-[2px] rounded-md' : 'border-{transparent}'
      } pl-[5px] bg-transparent w-full`;
    };
    return mstUser.map((user: MstUser, index: number) => {
      const isLastItem = index === mstUser.length - 1;

      return (
        <tr className="hover:opacity-80 odd:bg-[#e2efd9]" key={index}>
          <td className="p-[8px] border-[1px] border-solid border-[#fff]">
            <input
              className={styleInput(user.error.isErrorMstMail)}
              onChange={(event) => {
                if (event.target.value.length > 300) return;
                handleChangeUser(index, { mu_mail: event.target.value });
              }}
              type="text"
              disabled={user.recordState !== RecordState.INSERT}
              name="mu_mail"
              min={1}
              max={300}
              value={user.mu_mail ?? ''}
            />
          </td>
          <td className="p-[8px] border-[1px] border-solid border-[#fff]">
            <input
              className={`${styleInput(user.error.isErrorUserCd)} text-right`}
              onChange={(event) => {
                if (event.target.value.length > 8) return;
                handleChangeUser(index, { mu_user_id: event.target.value });
              }}
              type="number"
              name="mu_user_id"
              min={1}
              max={99999999}
              value={user.mu_user_id || ''}
              disabled={user.isCannotEditUserCd}
            />
          </td>
          <td className="p-[8px] border-[1px] border-solid border-[#fff]">
            <input
              className={styleInput(user.error.isErrorUserNm)}
              onChange={(event) => handleChangeUser(index, { mu_user_nm: event.target.value })}
              type="text"
              name="name"
              maxLength={50}
              value={user.mu_user_nm}
            />
          </td>
          <td
            className="p-[8px] border-[1px] border-solid border-[#fff] cursor-pointer"
            onClick={() => setModal({ open: true, index })}
          >
            <div className={styleInput(false)}>{user.mo_office_nm}</div>
          </td>

          <td className="p-[8px] border-[1px] border-solid border-[#fff]">
            <select
              className={styleInput(false)}
              onChange={(event) => handleChangeUser(index, { mu_admin_kb: event.target.value })}
              name="availableKb"
              value={user.mu_admin_kb ?? ''}
            >
              <option value="0">×</option>
              <option value="1">○</option>
            </select>
          </td>
          <td className="p-[8px] border-[1px] border-solid border-[#fff] text-center">
            <button onClick={() => onPressConfirmRemove(index)}>
              {!isLastItem ? <img src={images.icDelete} alt="" className="w-[20px]" /> : null}
            </button>
          </td>
        </tr>
      );
    });
  }, [mstUser]);

  const checkError = (itemUser: MstUser) => {
    const isErrorUserCd = itemUser.mu_user_id === null;
    const isErrorUserNm = itemUser.mu_user_nm === '';
    const isErrorMstMail = itemUser.mu_mail === '';

    return { isErrorUserCd, isErrorUserNm, isErrorMstMail };
  };

  const isExistItem = () => {
    const newListUser = [...mstUser];

    const mailArr = newListUser.map(function (item) {
      return item.mu_mail;
    });
    const idArr = newListUser.map(function (item) {
      return item.mu_user_id;
    });

    // 上記のどちらの配列も、現在表示されているテーブルと要素数は同じになる。
    // なので、for文で同時にチェックしていく。iはテーブルのindexと同義。
    let isExist = false;
    for (let i = 0; i++; mailArr.length - 1) {
      if (mailArr.indexOf(mailArr[i]) !== i) {
        newListUser[i].error.isErrorMstMail = true;
        isExist = true;
        break;
      }
      if (idArr.indexOf(idArr[i]) !== i) {
        newListUser[i].error.isErrorUserCd = true;
        isExist = true;
        break;
      }
    }
    setMstUser(newListUser);
    return isExist;
  };

  const validates = () => {
    const newListUser = [...mstUser];
    newListUser.pop();
    const newListAddError = newListUser.map((itemUser: MstUser) => {
      return { ...itemUser, error: checkError(itemUser) };
    });
    setMstUser(newListAddError);
    const indexItemError = newListUser.findIndex(
      (itemUser: MstUser) => itemUser.mu_user_id === null || itemUser.mu_user_nm === '' || itemUser.mu_mail === ''
    );
    if (indexItemError !== -1) {
      showAlertMessage('', ErrorMessage.INPUT_ERROR, InfoWord.ERROR, InfoWord.OK);
      return false;
    }
    if (isExistItem()) {
      showAlertMessage('', InfoMessage.REGISTR_DUPLICATION, InfoWord.ERROR, InfoWord.OK);
      return false;
    }
    return true;
  };

  const prepareDataApplyApi = (userData: MstUser) => {
    return {
      mu_user_id: userData.mu_user_id,
      mu_user_nm: userData.mu_user_nm,
      mu_mail: userData.mu_mail,
      mu_admin_kb: userData.mu_admin_kb,
      mu_office_cd: userData.mu_office_cd,
    };
  };

  const executeRegister = async () => {
    const mstUserNewList = [...mstUser];
    const insertData = mstUserNewList.filter((item: MstUser) => item.recordState === RecordState.INSERT);
    const updateData = mstUserNewList.filter((item: MstUser) => item.recordState === RecordState.UPDATE);

    // 最終行(新規行)を除外
    insertData.pop();

    const delSet: MutationSet<MstUser> = {
      recordState: RecordState.DELETE,
      data: removedMstUser,
      func: (data: MstUser) => deleteFunc({ variables: { input: { mu_mail: data.mu_mail } } }),
    };
    const updSet: MutationSet<MstUser> = {
      recordState: RecordState.UPDATE,
      data: updateData,
      func: (data: MstUser) => updateFunc({ variables: { input: prepareDataApplyApi(data) } }),
    };
    const insSet: MutationSet<MstUser> = {
      recordState: RecordState.INSERT,
      data: insertData,
      func: (data: MstUser) => insertFunc({ variables: { input: prepareDataApplyApi(data) } }),
    };

    const searchFunc = () => searchMstUser({ variables: { input: {} } });

    registerData<MstUser>({
      mutationSets1: [delSet, updSet, insSet],
      validates,
      searchFunc,
    });
  };

  const onPressConfirmRegister = () => {
    Auth.currentAuthenticatedUser()
      .then(() =>
        confirmAlert(InfoMessage.CONFIRM_REGISTR, '', InfoWord.WARNING, InfoWord.YES, InfoWord.NO, executeRegister)
      )
      .catch(() => {
        signOut();
        navigate(LOGIN_PATH, { replace: true, state: { from: location } });
      });
  };

  const isEmptyData = (data: MstUser) => {
    return data.mu_user_id === null && data.mu_user_nm === '' && data.mu_mail === '' && data.mu_office_cd === null;
  };

  const handleChangeUser = (index: number, value: { [key: string]: string }) => {
    const newListMstUser = [...mstUser];
    newListMstUser[index] = { ...newListMstUser[index], ...value };
    const errorObject: ErrorMstUser = { ...newListMstUser[index].error };

    switch (value.key) {
      case 'mu_user_id':
        errorObject.isErrorUserCd = false;
        break;
      case 'mu_user_nm':
        errorObject.isErrorUserNm = false;
        break;
      case 'mu_mail':
        errorObject.isErrorMstMail = false;
        break;
    }

    newListMstUser[index].error = { ...newListMstUser[index].error, ...errorObject };
    if (newListMstUser[index].isBeginData) {
      newListMstUser[index].recordState = RecordState.UPDATE;
    }

    setMstUser(newListMstUser);
  };

  const onCallbackFunction = (data: MstOffice) => {
    if (!modal.index) return;
    handleChangeUser(modal.index, { mu_office_cd: data.mo_office_cd, mo_office_nm: data.mo_office_nm });
  };

  const onPressRemove = async (index: number) => {
    const newListUser = [...mstUser];
    if (index < beginLengthListUser) {
      const newListRemove = [...removedMstUser];
      const dataItem = mstUser[index];
      setBeginLengthListUser(beginLengthListUser - 1);
      newListRemove.push({ ...dataItem, recordState: RecordState.DELETE });
      setRemovedMstUser(newListRemove);
    }
    newListUser.splice(index, 1);
    setMstUser(newListUser);
  };

  const onPressConfirmRemove = (index: number) => {
    confirmAlert(InfoMessage.CONFIRM_REMOVE, '', InfoWord.WARNING, InfoWord.YES, InfoWord.NO, () =>
      onPressRemove(index)
    );
  };

  useEffect(() => {
    if (mstUser?.length < 1) return;
    const lastIndex = mstUser.length - 1;
    const newListMstUser = [...mstUser];

    if (!isEmptyData(newListMstUser[lastIndex])) {
      newListMstUser.push(emptyUser);
      setMstUser(newListMstUser);
    }
  }, [mstUser]);

  useEffect(() => {
    if (!mstUserData) return;
    const newListUser: MstUser[] = mstUserData.getUserInfo.map((item: MstUser) => {
      return {
        ...item,
        recordState: RecordState.NONE,
        isCannotEditUserCd: true,
        error: defaultErrorMstUser,
        isBeginData: true,
      };
    });

    setBeginLengthListUser(mstUserData.getUserInfo.length);
    newListUser.sort((a: MstUser, b: MstUser) => Number(a.mu_user_id) - Number(b.mu_user_id));
    newListUser.push(emptyUser);
    setMstUser(newListUser);
  }, [mstUserData]);

  useEffect(() => {
    searchMstUser({ variables: { input: {} } });
  }, []);

  return (
    <MainLayout>
      <>
        <div className="flex flex-col items-center">
          <p className="p-[20px]">ユーザー管理</p>
          <div className="flex w-4/5">
            <button
              onClick={onPressConfirmRegister}
              className="border-[#000000] border-[1px] p-[10px] mt-[25px] mb-[25px] text-center h-[50px] w-[100px] flex justify-center items-center"
            >
              <b>登録</b>
            </button>
          </div>
          <table className="border-collapse border-[3px] border-solid border-[#ddd] w-4/5 td:p-[10px]">
            <thead>
              <tr className="hover:opacity-80 even:bg-[#e2efd9]">{renderTableHeader}</tr>
            </thead>
            <tbody>
              {isMstUserLoading ? (
                <tr className="hover:opacity-80 even:bg-[#e2efd9]">
                  <td
                    colSpan={header.length}
                    className="text-center animate-pulse p-[8px] border-[1px] border-solid border-[#fff]"
                  >
                    Loading...
                  </td>
                </tr>
              ) : (
                renderTableData
              )}
            </tbody>
          </table>
        </div>
        <Dialog open={modal.open} onClose={() => setModal({ open: true })} className="relative z-50 w-full h-full">
          <div className="fixed w-full h-full inset-0 flex items-center justify-center">
            <ModalGuideOffice
              setIsOpen={(value: boolean) => setModal({ ...modal, open: value })}
              onCallbackFunction={onCallbackFunction}
            />
          </div>
        </Dialog>
      </>
    </MainLayout>
  );
};

export default UserManagement;
